'use client';

import { MoreHorizontal, Bookmark, Bell, BellMinus, BellPlus, Flag } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { Spinner } from '@/components/ui/spinner';
import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

interface CardMoreMenuProps {
  starred: boolean;
  onStar: (e: React.MouseEvent) => void;
  following?: boolean;
  followLoading?: boolean;
  onFollow?: (e: React.MouseEvent) => void;
  followLabel?: { follow: string; unfollow: string };
  /** Opens the report flow (FlagReportModal owned by the caller). */
  onReport?: (e: React.MouseEvent) => void;
  className?: string;
}

export function CardMoreMenu({
  starred,
  onStar,
  following,
  followLoading,
  onFollow,
  followLabel = { follow: 'Follow', unfollow: 'Unfollow' },
  onReport,
  className,
}: CardMoreMenuProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const toggle = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(v => !v);
  }, []);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const menuWidth = 160;
    let left = rect.right - menuWidth;
    if (left < 8) left = 8;
    setPos({ top: rect.bottom + 4, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleScroll = () => setOpen(false);
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  const dropdown = open && pos && createPortal(
    <div
      ref={menuRef}
      className="fixed w-40 bg-popover border border-border rounded-lg shadow-lg z-[9999] py-1 text-sm"
      style={{ top: pos.top, left: pos.left }}
    >
      {onFollow && (
        <button
          onClick={(e) => { if (!followLoading) { onFollow(e); setOpen(false); } }}
          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-left group/followmenu"
        >
          {followLoading ? (
            <Spinner size="md" />
          ) : following ? (
            <>
              <Bell className="h-4 w-4 fill-current group-hover/followmenu:hidden" />
              <BellMinus className="h-4 w-4 hidden group-hover/followmenu:block" />
            </>
          ) : (
            <>
              <Bell className="h-4 w-4 group-hover/followmenu:hidden" />
              <BellPlus className="h-4 w-4 hidden group-hover/followmenu:block" />
            </>
          )}
          {following ? (
            <>
              <span className="group-hover/followmenu:hidden">Following</span>
              <span className="hidden group-hover/followmenu:inline">Unfollow</span>
            </>
          ) : followLabel.follow}
        </button>
      )}
      <button
        onClick={(e) => { onStar(e); setOpen(false); }}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-left"
      >
        {starred ? <Bookmark className="h-4 w-4 fill-current" /> : <Bookmark className="h-4 w-4" />}
        {starred ? t('discover.scopeSaved') : t('actions.save')}
      </button>
      {onReport && (
        <button
          onClick={(e) => { onReport(e); setOpen(false); }}
          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent hover:text-red-500 transition-colors text-left border-t border-border/50"
        >
          <Flag className="h-4 w-4" />
          Report
        </button>
      )}
    </div>,
    document.body,
  );

  return (
    <div className={className || ''}>
      <button
        ref={btnRef}
        onClick={toggle}
        className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-accent/80 transition-colors text-muted-foreground hover:text-foreground"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {dropdown}
    </div>
  );
}
